import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Grid,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    LinearProgress,
    CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import { LysepAPI } from '../../../../contexts/ApiContext';
import LocationSearch from './LocationSearch';
import ResultDataGrid from './ResultDataGrid';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
    }
}));

const FIELD_ORDER = ['il', 'ilce', 'semt', 'mahalle'];

const SearchByLocation = () => {
    const [searchConditions, setSearchConditions] = useState([
        { id: 1, field: 'il, value: ' }
    ]);

    const [additionalFields, setAdditionalFields] = useState({
        sokak: '',
        kapiNo: '',
        daireNo: ''
    });

    const [cities, setCities] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [semts, setSemts] = useState([]);
    const [neighborhoods, setNeighborhoods] = useState([]);
    const [loading, setLoading] = useState({
        cities: false,
        districts: false,
        semts: false,
        neighborhoods: false,
        search: false
    });

    const [searchResults, setSearchResults] = useState([]);

    const [searchStatus, setSearchStatus] = useState({
        loading: false,
        message: ''
    });

    const [error, setError] = useState(null);

    // İlleri getir
    const fetchCities = async () => {
        setLoading(prev => ({ ...prev, cities: true }));
        try {
            const response = await LysepAPI.get('/api/piratecall/helpers/cities');
            if (response.data.state) {
                setCities(response.data.cities);
            }
        } catch (error) {
            console.error('İller yüklenirken hata:', error);
        } finally {
            setLoading(prev => ({ ...prev, cities: false }));
        }
    };

    // İlçeleri getir
    const fetchDistricts = async (cityId) => {
        setLoading(prev => ({ ...prev, districts: true }));
        try {
            const response = await LysepAPI.post('/api/piratecall/helpers/districts', { cityId });
            if (response.data.state) {
                setDistricts(response.data.districts);
            }
        } catch (error) {
            console.error('İlçeler yüklenirken hata:', error);
        } finally {
            setLoading(prev => ({ ...prev, districts: false }));
        }
    };

    // Semtleri getir
    const fetchSemts = async (districtId) => {
        setLoading(prev => ({ ...prev, semts: true }));
        try {
            const response = await LysepAPI.post('/api/piratecall/helpers/neighborhoods', { districtId });
            if (response.data.state) {
                setSemts(response.data.neighborhoods);
            }
        } catch (error) {
            console.error('Semtler yüklenirken hata:', error);
        } finally {
            setLoading(prev => ({ ...prev, semts: false }));
        }
    };

    // Mahalleleri getir
    const fetchNeighborhoods = async (semtId) => {
        setLoading(prev => ({ ...prev, neighborhoods: true }));
        try {
            const response = await LysepAPI.post('/api/piratecall/helpers/streets', { neighborhoodId: semtId });
            if (response.data.state) {
                setNeighborhoods(response.data.streets);
            }
        } catch (error) {
            console.error('Mahalleler yüklenirken hata:', error);
        } finally {
            setLoading(prev => ({ ...prev, neighborhoods: false }));
        }
    };

    useEffect(() => {
        fetchCities();
    }, []);

    const handleAddCondition = () => {
        const newId = searchConditions.length > 0 
            ? Math.max(...searchConditions.map(c => c.id)) + 1 
            : 1;
        
        const lastCondition = searchConditions[searchConditions.length - 1];
        const lastFieldIndex = FIELD_ORDER.indexOf(lastCondition.field);
        const nextField = FIELD_ORDER[lastFieldIndex + 1];

        if (nextField) {
            setSearchConditions([
                ...searchConditions,
                { id: newId, field: nextField, value: '' }
            ]);
        }
    };

    const handleRemoveCondition = (id) => {
        const conditionToRemove = searchConditions.find(c => c.id === id);
        const conditionIndex = searchConditions.findIndex(c => c.id === id);
        
        // Kaldırılan alandan sonraki tüm alanları da kaldır
        const updatedConditions = searchConditions.slice(0, conditionIndex);
        
        // Bağımlı listeleri temizle
        if (conditionToRemove.field === 'il') {
            setDistricts([]);
            setSemts([]);
            setNeighborhoods([]);
        } else if (conditionToRemove.field === 'ilce') {
            setSemts([]);
            setNeighborhoods([]);
        } else if (conditionToRemove.field === 'semt') {
            setNeighborhoods([]);
        }

        setSearchConditions(updatedConditions);
    };

    const handleFieldChange = (id, field) => {
        const currentIndex = searchConditions.findIndex(condition => condition.id === id);
        const fieldIndex = FIELD_ORDER.indexOf(field);
        
        const updatedConditions = searchConditions.slice(0, currentIndex + 1);
        updatedConditions[currentIndex] = { ...updatedConditions[currentIndex], field, value: '' };

        // Seçilen alandan sonraki alanları kaldır
        setSearchConditions(updatedConditions);

        // İlgili listeleri temizle
        if (field === 'il') {
            setDistricts([]);
            setSemts([]);
            setNeighborhoods([]);
        } else if (field === 'ilce') {
            setSemts([]);
            setNeighborhoods([]);
        } else if (field === 'semt') {
            setNeighborhoods([]);
        }
    };

    const handleValueChange = async (id, value, field) => {
        const currentIndex = searchConditions.findIndex(condition => condition.id === id);
        const fieldIndex = FIELD_ORDER.indexOf(field);

        const updatedConditions = searchConditions.map(condition => 
            condition.id === id ? { ...condition, value } : condition
        );

        // Seçilen alandan sonraki alanların değerlerini sıfırla
        for (let i = currentIndex + 1; i < updatedConditions.length; i++) {
            updatedConditions[i] = { ...updatedConditions[i], value: '' };
        }

        setSearchConditions(updatedConditions);

        // İlgili verileri getir
        if (field === 'il' && value) {
            await fetchDistricts(value);
            setSemts([]);
            setNeighborhoods([]);
        } else if (field === 'ilce' && value) {
            await fetchSemts(value);
            setNeighborhoods([]);
        } else if (field === 'semt' && value) {
            await fetchNeighborhoods(value);
        }
    };

    const handleAdditionalFieldChange = (field, value) => {
        setAdditionalFields(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSearch = async () => {
        try {
            setSearchStatus({ loading: true, message: 'Arama yapılıyor, bu işlem biraz zaman alabilir...' });
            setError(null);

            // Boş olmayan alanları topla
            const searchParams = {};
            
            if (additionalFields.sokak?.trim()) {
                searchParams.sokak = additionalFields.sokak.trim();
            }
            if (additionalFields.kapiNo?.trim()) {
                searchParams.kapiNo = additionalFields.kapiNo.trim();
            }
            if (additionalFields.daireNo?.trim()) {
                searchParams.daireNo = additionalFields.daireNo.trim();
            }

            // İlçe ve mahalle seçili ise ekle
            const ilceCondition = searchConditions.find(condition => condition.field === 'ilce');
            if (ilceCondition?.value) {
                const selectedIlce = districts.find(d => d.id === ilceCondition.value);
                if (selectedIlce?.name?.trim()) {
                    searchParams.ilce = selectedIlce.name.trim();
                }
            }

            const mahalleCondition = searchConditions.find(condition => condition.field === 'mahalle');
            if (mahalleCondition?.value) {
                const selectedMahalle = neighborhoods.find(n => n.id === mahalleCondition.value);
                if (selectedMahalle?.name?.trim()) {
                    searchParams.mahalle = selectedMahalle.name.trim();
                }
            }

            // En az bir arama kriteri olmalı
            if (Object.keys(searchParams).length === 0) {
                setError('Lütfen en az bir arama kriteri girin');
                setSearchStatus({ loading: false, message: null });
                return;
            }

            const response = await LysepAPI.post('/api/piratecall/query/adres', searchParams, {
                timeout: 1800000 // 30 dakika
            });

            if (response.data.success && response.data.data && response.data.data.length > 0) {
                setSearchResults(response.data.data);
                setSearchStatus({ loading: false, message: `${response.data.data.length} sonuç bulundu` });
            } else {
                setSearchResults([]);
                setSearchStatus({ loading: false, message: 'Sonuç bulunamadı' });
            }
        } catch (error) {
            console.error('Arama hatası:', error);
            setError(error.response?.data?.message || 'Arama sırasında bir hata oluştu');
            setSearchStatus({ loading: false, message: 'Arama başarısız oldu' });
        }
    };

    const generateAddressString = () => {
        const addressParts = [];
        
        searchConditions.forEach(condition => {
            const options = getOptionsForField(condition.field);
            const selectedOption = options.find(opt => opt.id === condition.value);
            if (selectedOption) {
                addressParts.push(selectedOption.name);
            }
        });

        // Ek alanları ekle
        if (additionalFields.sokak) addressParts.push(additionalFields.sokak);
        if (additionalFields.kapiNo) addressParts.push(`No: ${additionalFields.kapiNo}`);
        if (additionalFields.daireNo) addressParts.push(`Daire: ${additionalFields.daireNo}`);

        return addressParts.join(', ');
    };

    const getOptionsForField = (field) => {
        switch (field) {
            case 'il':
                return cities;
            case 'ilce':
                return districts;
            case 'semt':
                return semts;
            case 'mahalle':
                return neighborhoods;
            default:
                return [];
        }
    };

    const getFieldLabel = (field) => {
        switch (field) {
            case 'il':
                return 'İl';
            case 'ilce':
                return 'İlçe';
            case 'semt':
                return 'Semt';
            case 'mahalle':
                return 'Mahalle';
            default:
                return '';
        }
    };

    const columns = [
        { field: 'TC', headerName: 'TC', width: 130 },
        { field: 'ADI', headerName: 'Adı', width: 130 },
        { field: 'SOYADI', headerName: 'Soyadı', width: 130 },
        { field: 'DOGUMTARIHI', headerName: 'Doğum Tarihi', width: 130 },
        { field: 'NUFUSIL', headerName: 'Nüfus İl', width: 130 },
        { field: 'NUFUSILCE', headerName: 'Nüfus İlçe', width: 130 },
        { field: 'ANNEADI', headerName: 'Anne Adı', width: 130 },
        { field: 'ANNETC', headerName: 'Anne TC', width: 130 },
        { field: 'BABAADI', headerName: 'Baba Adı', width: 130 },
        { field: 'BABATC', headerName: 'Baba TC', width: 130 },
        { field: 'UYRUK', headerName: 'Uyruk', width: 130 }
    ];

    return (
        <StyledPaper>
            <Typography variant="h6" gutterBottom>
                Adres Sorgusu
            </Typography>
            
            <Box sx={{ mb: 3 }}>
                {searchConditions.map((condition) => (
                    <Grid container spacing={2} key={condition.id} sx={{ mb: 2, alignItems: 'center' }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Alan</InputLabel>
                                <Select
                                    value={condition.field}
                                    label="Alan"
                                    onChange={(e) => handleFieldChange(condition.id, e.target.value)}
                                >
                                    {FIELD_ORDER.map((field) => (
                                        <MenuItem 
                                            key={field} 
                                            value={field}
                                            disabled={FIELD_ORDER.indexOf(field) < FIELD_ORDER.indexOf(condition.field)}
                                        >
                                            {getFieldLabel(field)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} sm={5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{getFieldLabel(condition.field)}</InputLabel>
                                <Select
                                    value={condition.value}
                                    label={getFieldLabel(condition.field)}
                                    onChange={(e) => handleValueChange(condition.id, e.target.value, condition.field)}
                                    disabled={loading[condition.field]}
                                >
                                    {getOptionsForField(condition.field).map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {loading[condition.field] && (
                                    <CircularProgress
                                        size={20}
                                        sx={{
                                            position: 'absolute',
                                            right: 30,
                                            top: '50%',
                                            transform: 'translateY(-50%)'
                                        }}
                                    />
                                )}
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} sm={1}>
                            <IconButton 
                                color="error" 
                                onClick={() => handleRemoveCondition(condition.id)}
                                disabled={searchConditions.length === 1}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                ))}

                {/* Ek alanlar */}
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Sokak"
                            value={additionalFields.sokak}
                            onChange={(e) => handleAdditionalFieldChange('sokak', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Kapı No"
                            value={additionalFields.kapiNo}
                            onChange={(e) => handleAdditionalFieldChange('kapiNo', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Daire No"
                            value={additionalFields.daireNo}
                            onChange={(e) => handleAdditionalFieldChange('daireNo', e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 3 }}>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddCondition}
                    disabled={searchConditions.length === FIELD_ORDER.length}
                >
                    Alan Ekle
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading.search}
                >
                    {loading.search ? <CircularProgress size={24} /> : 'Sorgula'}
                </Button>
            </Box>

            {/* Yükleme ve durum göstergesi */}
            {searchStatus.loading && (
                <Box sx={{ width: '100%', mb: 2 }}>
                    <LinearProgress />
                </Box>
            )}
            {searchStatus.message && (
                <Alert 
                    severity={searchStatus.loading ? "info" : searchResults.length > 0 ? "success" : "warning"}
                    sx={{ mb: 2 }}
                >
                    {searchStatus.message}
                </Alert>
            )}

            {/* Harita görünümü */}
            <Box sx={{ mt: 3, mb: 3 }}>
                <LocationSearch address={generateAddressString()} />
            </Box>

            {/* Sonuç tablosu */}
            {searchResults.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <ResultDataGrid
                        columns={columns}
                        data={searchResults}
                        onViewDetails={(row) => {
                            // TODO: Detay modalını aç
                            console.log('Detay:', row);
                        }}
                    />
                </Box>
            )}
        </StyledPaper>
    );
};

export default SearchByLocation; 